import React, { useCallback, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import type {
  MarketingRouterExperimentDecision,
  MarketingRouterMetricResult,
  MarketingRouterSignificance,
  MarketingRouterSignificanceTimeseriesDay,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import AuditEventSummary from '~/components/Common/AuditEventSummary';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { type MarketingRouterRuleRecord, useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import { useMarketingRouterSignificance } from '~/hooks/apis/promo/useMarketingRouterSignificance';
import { useMarketingRouterSignificanceTimeseries } from '~/hooks/apis/promo/useMarketingRouterSignificanceTimeseries';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import type { AuditLog } from '~/types/responses';

import MarketingRouterMetricTrendChart from './MarketingRouterMetricTrendChart';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import MarketingRouterVisitorsChart from './MarketingRouterVisitorsChart';
import {
  marketingRouterAuditEventTouchesRule,
  summarizeMarketingRouterAuditEvent,
} from './marketingRouterAuditChanges';
import {
  type LiftScale,
  METRIC_DISPLAY,
  type MarketingRouterMetricKey,
  POWER_RUNTIME_STATE_DISPLAY,
  formatConfidence,
  formatConfidenceFromP,
  formatDuration,
  formatEffectSize,
  formatExpectedDate,
  formatMetricTotal,
  formatMetricValue,
  formatRelativeLift,
  formatTrafficAllocation,
  formatVisitors,
  formatScope,
  formatVisitorsPerDay,
  getDaysRun,
  getDecisionSummary,
  getDetectableRelativeEffect,
  getLiftScale,
  getMetricRead,
  getMetricsInOrder,
  getPowerRuntimeEmptyReason,
  getPowerRuntimeRows,
  getPowerRuntimeSummary,
  getRuleRunStatus,
  getScopeOptions,
  getVisitorsPerArmPerDay,
  summariseVariantVisitors,
  toLiftScalePercent,
  totalVisitors,
} from './marketingRouterSignificanceDisplay';

type RouteParams = {
  ruleId: string;
};

function formatDate(date: string): string {
  return dayjs(date).format('DD MMM YYYY');
}

function HeaderTile({ label, value, caption }: { label: string; value: React.ReactNode; caption?: string }) {
  return (
    <Grid size={{ xs: 6, md: 2.4 }}>
      <Stack spacing={0.5}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" component="div">
          {value}
        </Typography>
        {caption ? (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        ) : null}
      </Stack>
    </Grid>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((item) => (
        <Box component="li" key={item}>
          <Typography variant="body2">{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function DecisionCard({
  decision,
  results,
  baseVariantName,
  daysRun,
}: {
  decision: MarketingRouterExperimentDecision;
  results: MarketingRouterMetricResult[];
  baseVariantName: string;
  daysRun: number;
}) {
  const summary = getDecisionSummary(decision, results, baseVariantName, daysRun);
  return (
    <Alert severity={summary.severity} variant="outlined" sx={{ alignItems: 'flex-start' }}>
      <AlertTitle>{summary.title}</AlertTitle>
      <Bullets items={summary.bullets} />
    </Alert>
  );
}

function liftColor(result: MarketingRouterMetricResult): string {
  if (result.verdict !== 'SIGNIFICANT') {
    return 'text.primary';
  }
  return result.absoluteLift >= 0 ? 'success.main' : 'error.main';
}

/** The tick labels along the confidence-interval column, in the header. */
function LiftAxis({ scale }: { scale: LiftScale }) {
  const ticks = [scale.min, scale.min / 2, 0, scale.max / 2, scale.max];
  return (
    <Box sx={{ position: 'relative', height: 16, mt: 0.5 }}>
      {ticks.map((tick) => (
        <Typography
          key={tick}
          variant="caption"
          color="text.secondary"
          sx={{ position: 'absolute', left: `${toLiftScalePercent(tick, scale)}%`, transform: 'translateX(-50%)' }}
        >
          {formatRelativeLift(tick)}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * One row's 95% CI on the shared axis: a bar from lower to upper bound with a
 * tick at the estimate. The detectable band is shaded behind it so a CI that
 * sits entirely inside the unshaded middle reads as "not enough traffic yet".
 */
function LiftIntervalCell({ result, scale }: { result: MarketingRouterMetricResult; scale: LiftScale }) {
  const ci = result.relativeCi95;
  if (!ci || ci.length !== 2 || result.relativeLift === null) {
    return <Typography variant="body2">—</Typography>;
  }
  const detectable = getDetectableRelativeEffect(result);
  const at = (value: number) => `${toLiftScalePercent(value, scale)}%`;
  const tooltip = `95% CI ${formatRelativeLift(ci[0])} to ${formatRelativeLift(ci[1])}${
    detectable !== null ? ` · detectable at current traffic: ${formatEffectSize(detectable)} or more` : ''
  }`;

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ position: 'relative', height: 24 }}>
        {detectable !== null ? (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                width: at(-detectable),
                top: 4,
                height: 16,
                bgcolor: 'action.hover',
              }}
            />
            <Box
              sx={{ position: 'absolute', left: at(detectable), right: 0, top: 4, height: 16, bgcolor: 'action.hover' }}
            />
          </>
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            left: at(0),
            top: 0,
            height: '100%',
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: at(ci[0]),
            width: `calc(${at(ci[1])} - ${at(ci[0])})`,
            top: 9,
            height: 6,
            borderRadius: 3,
            bgcolor: result.verdict === 'SIGNIFICANT' ? liftColor(result) : 'text.disabled',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: at(result.relativeLift),
            top: 6,
            width: 2,
            height: 12,
            ml: '-1px',
            bgcolor: 'text.primary',
          }}
        />
      </Box>
    </Tooltip>
  );
}

function ImprovementCell({ result }: { result: MarketingRouterMetricResult }) {
  if (result.relativeLift === null) {
    return <Typography variant="body2">—</Typography>;
  }
  const Icon = result.absoluteLift >= 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Icon fontSize="inherit" sx={{ color: liftColor(result) }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: liftColor(result) }}>
        {formatRelativeLift(result.relativeLift)}
      </Typography>
    </Stack>
  );
}

function ConfidenceCell({ result }: { result: MarketingRouterMetricResult }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ fontWeight: result.verdict === 'SIGNIFICANT' ? 600 : 400 }}>
        {formatConfidence(result)}
      </Typography>
      {result.verdict === 'SIGNIFICANT' ? (
        <Chip label="Significant" color={result.absoluteLift >= 0 ? 'success' : 'error'} size="small" />
      ) : null}
      {result.verdict === 'COLLECTING' ? <Chip label="Collecting" color="info" size="small" /> : null}
    </Stack>
  );
}

/**
 * One metric as Optimizely lays it out: a row per variation, control first,
 * with the confidence interval drawn inline on a shared axis that lives in
 * the column header. The read-out below is one bullet per point.
 */
function MetricTable({
  metric,
  position,
  results,
  baseVariantName,
  daysRun,
  graph,
}: {
  metric: MarketingRouterMetricKey;
  position: number;
  results: MarketingRouterMetricResult[];
  baseVariantName: string;
  daysRun: number;
  /** The over-time graph for this metric; folded away until asked for. */
  graph?: React.ReactNode;
}) {
  const [graphOpen, setGraphOpen] = useState(false);
  const display = METRIC_DISPLAY[metric];
  const base = results[0];
  const tier = base?.tier;
  const scale = getLiftScale(results);
  const conversionsLabel = metric === 'purchase' ? 'Purchases' : display.totalLabel;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip label={position} size="small" color={tier === 'primary' ? 'primary' : 'default'} />
        <Typography variant="h3">{display.name}</Typography>
        {tier === 'primary' ? (
          <MarketingRouterTermLabel term="primaryMetric">
            <Chip label="Primary" size="small" variant="outlined" color="primary" />
          </MarketingRouterTermLabel>
        ) : null}
      </Stack>
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" aria-label={`${display.name} results`}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '16%' }}>Variations</TableCell>
              <TableCell sx={{ width: '12%' }}>{display.perVisitorLabel}</TableCell>
              <TableCell sx={{ width: '14%' }}>
                {conversionsLabel}
                <Typography variant="caption" color="text.secondary" display="block">
                  Visitors
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '12%' }}>Improvement</TableCell>
              <TableCell sx={{ width: '32%' }}>
                <Typography variant="body2" align="center" component="div">
                  Confidence interval
                </Typography>
                <LiftAxis scale={scale} />
              </TableCell>
              <TableCell>
                <MarketingRouterTermLabel term="confidence">Confidence</MarketingRouterTermLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {base ? (
              <TableRow>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{baseVariantName}</Typography>
                    <Chip label="Control" size="small" variant="outlined" />
                  </Stack>
                </TableCell>
                <TableCell>{formatMetricValue(metric, base.baseValue)}</TableCell>
                <TableCell>
                  {formatMetricTotal(metric, base.baseTotal)}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatVisitors(base.baseN)}
                  </Typography>
                </TableCell>
                <TableCell>—</TableCell>
                <TableCell />
                <TableCell>—</TableCell>
              </TableRow>
            ) : null}
            {results.map((result) => (
              <TableRow key={result.variantId}>
                <TableCell>
                  <Typography variant="body2">{result.variantName}</Typography>
                </TableCell>
                <TableCell>{formatMetricValue(metric, result.treatValue)}</TableCell>
                <TableCell>
                  {formatMetricTotal(metric, result.treatTotal)}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatVisitors(result.treatN)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ImprovementCell result={result} />
                </TableCell>
                <TableCell>
                  <LiftIntervalCell result={result} scale={scale} />
                </TableCell>
                <TableCell>
                  <ConfidenceCell result={result} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 0.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Read
        </Typography>
        <Stack spacing={1}>
          {results.map((result) => (
            <Bullets key={result.variantId} items={getMetricRead(result, daysRun)} />
          ))}
        </Stack>
      </Box>
      {graph ? (
        <Box>
          <Button
            size="small"
            onClick={() => setGraphOpen(!graphOpen)}
            endIcon={graphOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={graphOpen}
          >
            {graphOpen ? 'Hide graph' : 'Show graph'}
          </Button>
          <Collapse in={graphOpen} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 1 }}>{graph}</Box>
          </Collapse>
        </Box>
      ) : null}
    </Stack>
  );
}

const SUMMARY_TONE_COLOR = {
  success: 'success.main',
  warning: 'warning.main',
  neutral: 'text.secondary',
} as const;

/**
 * What the experiment can detect today, how many visitors each smaller
 * effect takes, and whether they will have arrived before the rule ends —
 * for whichever metric is selected, the primary one by default.
 */
function PowerRuntimeCard({
  significance,
  metricsInOrder,
  timeseriesDays,
  endDate,
  daysRun,
  now,
}: {
  significance: MarketingRouterSignificance;
  metricsInOrder: MarketingRouterMetricKey[];
  timeseriesDays: MarketingRouterSignificanceTimeseriesDay[];
  endDate: string;
  daysRun: number;
  now: dayjs.Dayjs;
}) {
  const primaryDecision = significance.decisions[0];
  const primaryMetric = primaryDecision?.primaryMetric ?? metricsInOrder[0];
  const [metric, setMetric] = useState<MarketingRouterMetricKey | undefined>(primaryMetric);
  if (!primaryDecision || !metric) {
    return null;
  }

  const result = significance.results.find(
    (candidate) => candidate.variantId === primaryDecision.variantId && candidate.metric === metric,
  );
  const perArm = result ? Math.min(result.baseN, result.treatN) : 0;
  const traffic = getVisitorsPerArmPerDay(timeseriesDays, now, { perArm, daysRun });
  const rows = result ? getPowerRuntimeRows({ result, visitorsPerDay: traffic.perDay, endDate, now }) : [];
  const ended = now.isAfter(dayjs(endDate), 'day');
  const summary = getPowerRuntimeSummary(rows, metric, ended);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="h2">Power &amp; runtime</Typography>
          <Typography variant="caption" color="text.secondary">
            80% power · 95% confidence
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Rule {ended ? 'ended' : 'ends'} {formatDate(endDate)}
        </Typography>
      </Stack>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={metric}
        onChange={(_, next: MarketingRouterMetricKey | null) => {
          if (next) {
            setMetric(next);
          }
        }}
        aria-label="Metric to forecast"
      >
        {metricsInOrder.map((key) => (
          <ToggleButton key={key} value={key}>
            {METRIC_DISPLAY[key].name}
            {key === primaryMetric ? ' · Primary' : ''}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {summary ? (
        <Typography variant="body1" sx={{ fontWeight: 500, color: SUMMARY_TONE_COLOR[summary.tone] }}>
          {summary.text}
        </Typography>
      ) : null}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {getPowerRuntimeEmptyReason(result, daysRun, significance.baseVariantName)}
        </Typography>
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxWidth: 800 }}>
          <Table size="small" aria-label="Power and runtime">
            <TableHead>
              <TableRow>
                <TableCell>To detect (relative)</TableCell>
                <TableCell align="right">Visitors / arm</TableCell>
                <TableCell align="right">Expected confidence</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const stateDisplay = POWER_RUNTIME_STATE_DISPLAY[row.state];
                return (
                  <TableRow key={row.targetEffect}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{formatEffectSize(row.targetEffect)}</Typography>
                        {row.detectableNow ? <Chip label="detectable today" size="small" variant="outlined" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {row.detectableNow
                        ? formatVisitors(row.visitorsPerArm)
                        : `~${formatVisitors(row.visitorsPerArm)}`}
                    </TableCell>
                    <TableCell align="right">
                      {row.state === 'reached' ? (
                        'now'
                      ) : (
                        <>
                          <Typography variant="body2">{formatExpectedDate(row.expectedOn, now)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.runtime}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.detectableNow ? null : (
                        <Chip
                          label={stateDisplay.label}
                          color={stateDisplay.color}
                          variant={stateDisplay.variant}
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="body2" color="text.secondary">
        At {formatVisitorsPerDay(traffic.perDay)} visitors per arm per day ({traffic.basis}). Halving the effect you
        want to see quadruples the visitors it takes.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Forecast based on current traffic and observed results. The date may change as more data is collected.
      </Typography>
    </Stack>
  );
}

function HealthTile({ health }: { health: MarketingRouterSignificance['health'] }) {
  return (
    <HeaderTile
      label="Health"
      value={
        <Tooltip
          title={`Sample-ratio check: ${formatConfidenceFromP(health.srm.pValue)} confidence that the split is off (chi-square ${health.srm.chiSquare.toFixed(2)})`}
        >
          <Chip
            label={health.srm.healthy ? 'Healthy' : 'Traffic split broken'}
            color={health.srm.healthy ? 'success' : 'error'}
            size="small"
          />
        </Tooltip>
      }
      caption={
        health.overlapRate !== undefined
          ? `${(health.overlapRate * 100).toFixed(1)}% of users also in another experiment`
          : undefined
      }
    />
  );
}

function HealthWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}
    </Stack>
  );
}

function RuleDetailContent({ rule }: { rule: MarketingRouterRuleRecord }) {
  // Product scope of the read: '' asks for the decision view (the endpoint
  // derives it from the rule's channel markup), anything else is a
  // `loyaltyOfferTypes` value. One control drives both requests so every
  // number on the page is counted over the same products.
  const [scopeValue, setScopeValue] = useState('');
  const query = { ruleId: rule.id, loyaltyOfferTypes: scopeValue || undefined };
  const significanceQuery = useMarketingRouterSignificance({
    marketingRouterId: rule.marketingRouterId,
    query,
  });
  const significance = significanceQuery.data;
  const timeseriesQuery = useMarketingRouterSignificanceTimeseries({
    marketingRouterId: rule.marketingRouterId,
    query,
  });
  const timeseriesDays = timeseriesQuery.data?.days ?? [];
  const scopeOptions = useMemo(() => (significance ? getScopeOptions(significance.scope) : []), [significance]);

  const now = dayjs();
  const status = getRuleRunStatus(rule.start_date, rule.end_date, now);
  const daysRun = getDaysRun(rule.start_date, rule.end_date, now);
  const allocation = formatTrafficAllocation(rule.variants.map((variant) => variant.weight));
  const visitorSummaries = useMemo(
    () =>
      significance
        ? summariseVariantVisitors(significance.results, significance.baseVariantId, significance.baseVariantName)
        : [],
    [significance],
  );
  const metricsInOrder = useMemo(() => (significance ? getMetricsInOrder(significance) : []), [significance]);

  // Audit events are stored against the router, which holds every rule for a
  // brand and region, so both callbacks narrow them to the rule on screen.
  const filterAuditLog = useCallback((log: AuditLog) => marketingRouterAuditEventTouchesRule(log, rule.id), [rule.id]);
  const summarizeAuditLog = useCallback((log: AuditLog) => summarizeMarketingRouterAuditEvent(log, rule.id), [rule.id]);

  return (
    <Stack spacing={4}>
      <AuditEventSummary
        title={rule.name}
        modelType="marketing_router"
        modelId={rule.marketingRouterId}
        // A router's events cover all of its rules, so a page of them can hold
        // few for this one; fetch deeper than the default to fill the list.
        limit={100}
        filterLog={filterAuditLog}
        summarizeLog={summarizeAuditLog}
      />
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <HeaderTile label="Status" value={<Chip label={status.label} color={status.color} size="small" />} />
            <HeaderTile
              label="Visitors"
              value={significance ? formatVisitors(totalVisitors(visitorSummaries)) : '…'}
              caption={
                visitorSummaries.length > 0
                  ? visitorSummaries
                      .map((summary) => `${summary.variantName}: ${formatVisitors(summary.visitors)}`)
                      .join(' · ')
                  : undefined
              }
            />
            <HeaderTile
              label="Duration"
              value={formatDuration(rule.start_date, rule.end_date, now)}
              caption={`${formatDate(rule.start_date)} – ${formatDate(rule.end_date)}`}
            />
            <HeaderTile label="Traffic allocation" value={allocation} caption={`${rule.variants.length} variants`} />
            {significance ? <HealthTile health={significance.health} /> : null}
          </Grid>
          {significance ? <HealthWarnings warnings={significance.health.warnings} /> : null}
        </CardContent>
      </Card>

      {significanceQuery.isLoading ? <Typography color="text.secondary">Loading experiment results…</Typography> : null}
      {significanceQuery.isError ? (
        <Alert severity="error">
          {significanceQuery.error instanceof Error
            ? significanceQuery.error.message
            : 'Failed to load experiment results.'}
        </Alert>
      ) : null}

      {significance ? (
        <>
          {scopeOptions.length > 1 ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                label="Products counted"
                select
                size="small"
                value={scopeValue}
                onChange={(event) => setScopeValue(event.target.value)}
                sx={{ minWidth: 280 }}
              >
                {scopeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {significance.scope.isDecisionView ? null : (
                <Chip label="Exploratory" color="warning" size="small" variant="outlined" />
              )}
              {significanceQuery.isFetching ? (
                <Typography variant="body2" color="text.secondary">
                  Updating…
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          <Stack spacing={1.5}>
            <Typography variant="h2">Decision</Typography>
            {significance.scope.isDecisionView ? (
              <>
                {significance.decisions.map((decision) => (
                  <DecisionCard
                    key={decision.variantId}
                    decision={decision}
                    results={significance.results}
                    baseVariantName={significance.baseVariantName}
                    daysRun={daysRun}
                  />
                ))}
                {significance.decisions.length === 0 ? (
                  <Alert severity="info">This rule has no treatment variants to compare against control.</Alert>
                ) : null}
              </>
            ) : (
              <Alert severity="info">
                Decisions are only made on the markup scope
                {significance.scope.treatmentLoyaltyOfferTypes
                  ? ` (${significance.scope.treatmentLoyaltyOfferTypes.join(', ')})`
                  : ''}
                . This view counts {formatScope(significance.scope)} and is exploratory.
              </Alert>
            )}
          </Stack>

          {metricsInOrder.length > 0 ? (
            <Stack spacing={3}>
              <Typography variant="h2">Decision-making metrics</Typography>
              {metricsInOrder.map((metric, index) => (
                <MetricTable
                  key={metric}
                  metric={metric}
                  position={index + 1}
                  results={significance.results.filter((result) => result.metric === metric)}
                  baseVariantName={significance.baseVariantName}
                  daysRun={daysRun}
                  graph={
                    timeseriesQuery.isLoading ? (
                      <Typography color="text.secondary" variant="body2">
                        Loading daily results…
                      </Typography>
                    ) : timeseriesQuery.isError ? (
                      <Alert severity="warning">Daily results could not be loaded.</Alert>
                    ) : (
                      <MarketingRouterMetricTrendChart
                        metric={metric}
                        days={timeseriesDays}
                        baseVariantName={significance.baseVariantName}
                      />
                    )
                  }
                />
              ))}
            </Stack>
          ) : null}

          {timeseriesDays.length > 0 ? (
            <MarketingRouterVisitorsChart days={timeseriesDays} baseVariantId={significance.baseVariantId} />
          ) : null}

          <PowerRuntimeCard
            significance={significance}
            metricsInOrder={metricsInOrder}
            timeseriesDays={timeseriesDays}
            endDate={rule.end_date}
            daysRun={daysRun}
            now={now}
          />
        </>
      ) : null}
    </Stack>
  );
}

export default function MarketingRouterRuleDetailPage() {
  const { ruleId } = useParams<RouteParams>();
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rule = useMemo(
    () => (rulesQuery.data ?? []).find((candidate) => candidate.id === ruleId),
    [rulesQuery.data, ruleId],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Router Rules / {rule?.name ?? 'Rule detail'}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={rule?.name ?? 'Marketing router rule'}>
          <Stack direction="row" spacing={1}>
            <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
              Back to rules
            </Button>
            {rule ? (
              <Button
                component={Link}
                to={`/marketing/marketing-router-rules/${rule.id}/edit?marketingRouterId=${encodeURIComponent(
                  rule.marketingRouterId,
                )}`}
                variant="outlined"
              >
                Edit rule
              </Button>
            ) : null}
          </Stack>
        </PageHeader>
        {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rule…</Typography> : null}
        {rulesQuery.isError ? (
          <Alert severity="error">
            {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rules.'}
          </Alert>
        ) : null}
        {!rulesQuery.isLoading && !rulesQuery.isError && !rule ? (
          <Alert severity="warning">Rule “{ruleId}” was not found for the current tenant.</Alert>
        ) : null}
        {rule ? <RuleDetailContent rule={rule} /> : null}
      </Container>
    </>
  );
}
